import { useCallback, useEffect, useRef, useState } from 'react'
import { logout } from '../lib/auth/session'
import { markLogoutStart, markLogoutReplaced, reportInterrupt } from '../lib/idleLogoutProbe'
// D-838 AC2: 最後の活動時刻の読み書きは lib/auth/sessionActivity.js に一本化した(ログアウト側からも消すため)。
//   ★読み書きの中身は D-360 のものそのまま。ここでの判定・間引きは 1 ビットも変えていない。
import { readSessionActivity, writeSessionActivity, clearSessionActivity } from '../lib/auth/sessionActivity'

// SPEC-AUTH-TIMEOUT-LOGOUT-S1-01: 無操作30分アイドル → logout
//   (ヒロ指示 2026-07-07: 15分→30分変更。2026-06-23の5分→15分を更新)
// SPEC-AUTH-TIMEOUT-REALTIME-RESUME-FIX-01: setInterval実時間検算 + 戻りイベント網羅
// SPEC-AUTH-TIMEOUT-HIDDEN-TIMESTAMP-FIX-01: hiddenAtRef で離席実時間を計測。
//   visible/pageshow 復帰時は lastActivityRef でなく hiddenAt 基準で判定し、
//   戻り際タッチ → resetActivity 競合を根本排除 (Page Visibility API 標準パターン)。
// SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: hide-on-hide/decide-on-return。hidden 化した瞬間に
//   isLocked=true (最後に描画されるフレームをカバーにする) → 復帰時に window 内なら
//   isLocked=false で即座に元画面へ、window 超過なら doLogout。前画面のフラッシュを構造的に排除。
const IDLE_MS = 30 * 60 * 1000
const CHECK_INTERVAL = 30 * 1000 // 実時間検算 polling 間隔
const EVENTS = ['click', 'touchstart', 'scroll', 'keydown', 'pointerdown']

// SPEC-IDLE-LOCK-RELOAD-DURABILITY-01 (D-360) SEGMENT_1: 離席判定をリロード耐性にする。
//   hiddenAtRef 方式(メモリ・SPEC-AUTH-TIMEOUT-HIDDEN-TIMESTAMP-FIX-01 の競合修正)は維持したまま、
//   最後の活動時刻を localStorage にも保存し起動時マウントでも評価する。これで iOS が PWA をメモリ破棄→
//   新規ロード(hidden イベントが当該ページに存在しない)した場合でも 30分門番が働く。
//   ★localStorage は「追加の守り」であって hiddenAtRef の置き換えではない。private mode 等では try/catch で
//     完全に無害化し、従来の hiddenAt 経路だけで動く(機能向上のつもりで既存を壊さない)。
// ★キーと読み書きは lib/auth/sessionActivity.js へ移した(D-838 AC2)。ここでは輸入して使うだけ。
const LS_WRITE_THROTTLE_MS = 10 * 1000   // scroll 連打の同期書き込みはカクつくため 10秒間隔に間引く(30分判定に10秒誤差は無視可)


export function useSessionLock(enabled = true) {
  const timerRef = useRef(null)
  const intervalRef = useRef(null)
  const lastActivityRef = useRef(Date.now()) // 前面での操作タイムスタンプ (フォアグラウンドidle用)
  const hiddenAtRef = useRef(null)           // hidden になった時刻 (離席実時間計測用)
  const lsWriteAtRef = useRef(0)             // D-360: localStorage への最終書込時刻 (スロットリング用)
  // SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: hidden 中はカバーを被せる。UI 専用フラグ (logout 判定には非関与)。
  const [isLocked, setIsLocked] = useState(false)

  const doLogout = useCallback(async () => {
    clearSessionActivity() // D-360: 次回ログインに古い lastActivity を持ち越さない (AC8)
    markLogoutStart()
    await logout()
    markLogoutReplaced()
    window.location.replace('/login')
  }, [])

  // D-360: localStorage への活動時刻書込。scroll 連打でカクつかないよう 10秒間隔に間引く(force=hidden 時は即書込)。
  //   メモリ上の lastActivityRef は呼び出し側で毎回更新済み。ここは localStorage のみ担当。
  const persistActivity = useCallback((ts, force = false) => {
    if (force || ts - lsWriteAtRef.current >= LS_WRITE_THROTTLE_MS) {
      lsWriteAtRef.current = ts
      writeSessionActivity(ts)
    }
  }, [])

  const scheduleTimer = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(doLogout, IDLE_MS)
  }, [doLogout])

  // 前面無操作用: setTimeoutの相対カウントを信用せずDate.now()-lastActivityで判定
  const checkElapsed = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current
    if (elapsed >= IDLE_MS) {
      doLogout()
    } else {
      scheduleTimer()
    }
  }, [doLogout, scheduleTimer])

  const resetActivity = useCallback(() => {
    if (!enabled) return
    const now = Date.now()
    lastActivityRef.current = now  // メモリは毎回更新 (従来通り)
    persistActivity(now)           // D-360: localStorage は 10秒間隔に間引いて追随
    scheduleTimer()
  }, [enabled, scheduleTimer, persistActivity])

  useEffect(() => {
    if (!enabled) {
      clearTimeout(timerRef.current)
      clearInterval(intervalRef.current)
      return
    }

    // D-360 SEGMENT_1: 起動時マウント評価。PWA 破棄後の新規ロード(hidden イベントが当該ページに無い)でも
    //   門番を効かせる。LS の最終活動が IDLE_MS 以上前なら即ログアウト。値が無い/壊れているなら巻き込まず初期化。
    const stored = readSessionActivity()
    if (stored != null && Date.now() - stored >= IDLE_MS) {
      setIsLocked(true) // D-360: clearStoredActivity 後も redirect まで前画面を出さない(staleOnOpen が消えても維持)
      doLogout()
      return  // ログアウト中: リスナー/タイマーを張らない
    }
    persistActivity(Date.now(), true) // 現在時刻で初期化/更新(初回ログイン直後を巻き込まない)

    scheduleTimer()
    // 定期実時間検算 (フォアグラウンド無操作 + iOS timer suspend 後の次 tick 判定)
    intervalRef.current = setInterval(checkElapsed, CHECK_INTERVAL)

    // 活動イベント: lastActivity 更新 + タイマー再スケジュール
    EVENTS.forEach(e => window.addEventListener(e, resetActivity, { passive: true }))

    // SPEC-AUTH-TIMEOUT-HIDDEN-TIMESTAMP-FIX-01: hidden 時刻を記録
    // pagehide も記録 (iOS bfcache でvisibilitychange未発火の取りこぼし防止)
    const handleHide = () => {
      const now = Date.now()
      hiddenAtRef.current = now
      persistActivity(now, true) // D-360: hidden の瞬間は必ず記録(離席開始=最後の活動時刻。リロード耐性の要)
      // SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: hidden と同じ同期 tick でカバーを立てる (復帰時に
      // 前画面が先に描画されないよう、backgrounding 完了前の最後のフレームをカバーにする)。
      setIsLocked(true)
    }

    // SPEC-AUTH-TIMEOUT-HIDDEN-TIMESTAMP-FIX-01: visible 復帰時は hiddenAt 基準で離席実時間を判定。
    //   戻り際に touchstart が先に走っても hiddenAt は操作で変化しないため競合排除。
    //   hiddenAt がなければ (フォアグラウンドでの focus 等) スキップ。
    const handleVisibleReturn = () => {
      if (hiddenAtRef.current === null) return
      if (Date.now() - hiddenAtRef.current >= IDLE_MS) {
        // window 超過: doLogout() は window.location.replace('/login') で全 state を破棄する
        // ため、カバーは張ったまま (isLocked=false にしない) → リダイレクトまで前画面を出さない。
        doLogout()
      } else {
        hiddenAtRef.current = null
        setIsLocked(false) // window 内: カバーを外し、離れた画面をそのまま即復帰 (remount なし)。
        scheduleTimer()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleHide()
      } else if (document.visibilityState === 'visible') {
        handleVisibleReturn()
      }
    }

    const handlePageshow = () => handleVisibleReturn()
    const handleFocus    = () => handleVisibleReturn()

    // SW controllerchange 観測 (reportInterrupt probe、恒常運用)
    let _swCleanup = null
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const onControllerChange = () => {
        reportInterrupt('SWCHANGE')
      }
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
      _swCleanup = () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handleHide)
    window.addEventListener('pageshow', handlePageshow)
    window.addEventListener('focus', handleFocus)

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, resetActivity))
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handleHide)
      window.removeEventListener('pageshow', handlePageshow)
      window.removeEventListener('focus', handleFocus)
      if (_swCleanup) _swCleanup()
      clearTimeout(timerRef.current)
      clearInterval(intervalRef.current)
    }
  }, [enabled, resetActivity, scheduleTimer, checkElapsed, doLogout, persistActivity])

  // D-360 AC_GATE4: 起動時に LS が既に stale なら、この render の時点でカバーを立てて業務画面を一瞬も見せない。
  //   effect の doLogout は非同期・useEffect は paint 後に走るため、render 時にも評価して isLoggedIn が false→true に
  //   なった最初の render(=業務画面が出得る最初の render)で即カバーを被せる。通常運用では LS は常に fresh なので false。
  const staleOnOpen = enabled
    ? (() => { const s = readSessionActivity(); return s != null && Date.now() - s >= IDLE_MS })()
    : false

  // SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: App.jsx がカバー表示に使う (既存の値無視 caller は不変)。
  return isLocked || staleOnOpen
}
